import React, { ReactElement, useContext, useEffect, useMemo, useRef, useState } from 'react'
import SearchBox from '@/components/ui/wizard/SearchBox'
import { Provider, useDispatch } from 'react-redux'
import {
  createWizardStore,
  MIN_NUMBER_OF_GUESTS,
  useWizardSelector,
  WizardState,
} from '@/components/ui/wizard/WizardState'
import {
  resetLocations,
  resetTopic,
  resetTravelPeriodSearch,
  setActiveStep,
  setLocationSearchResult,
  setLocationSearchTerm,
  setSavedSearchLoaded,
  setSearchBounds,
  setSelectedLocationItems,
  setSelectedTopicItems,
  setState,
  setTopicSearchResult,
  setTopicSearchTerm,
} from '@/components/ui/wizard/WizardActions'
import { Currency, SearchItem, SearchProps, SearchResultItemCategory } from '@/types/Page'
import _compact from 'lodash/compact'
import _groupBy from 'lodash/groupBy'
import _findKey from 'lodash/findKey'
import { AnyAction, Dispatch } from 'redux'
import { ActionCreatorWithOptionalPayload } from '@reduxjs/toolkit'
import moment, { Moment } from 'moment'
import WizardInput from '@/components/ui/wizard/WizardInput'
import {
  fetchWithAbort,
  getTimeSearchQueryGeneral,
  lockScrollableBody,
  onMobile,
  onMobileOrTablet,
  unlockScrollableBody,
} from '@/lib/utils/utils'
import {
  DESTINATION_INPUT_PROPS,
  SEARCH_PARAMETERS_UPDATED_EVENT_TYPE,
  SearchResult,
  TOPIC_INPUT_PROPS,
  TRAVEL_PERIOD_INPUT_PROPS,
  WizardStep,
} from '@/components/ui/wizard/Wizard'
import WizardWrapper from '@/components/ui/wizard/WizardWrapper'
import TravelPeriod, {
  DATE_FLEXIBILITY_OPTIONS_CALENDAR,
  DATE_FLEXIBILITY_OPTIONS_FLEXIBLE,
  TRAVEL_PERIOD_OPTIONS,
} from '@/components/ui/wizard/TravelPeriod'
import { ConfigContext } from '@/components/common/ConfigContext'
import SearchOptions from '@/components/ui/wizard/filter/SearchOptions'
import { RatingValues } from '@/components/ui/wizard/filter/Rating'
import {
  Domain,
  ExtendedDomainConfiguration,
  LocationFilterElement,
  LocationFilterElementType,
} from '../../../domain-config/domain-config'
import { Duration, Month, SEARCH_PARAMETER_KEYS, SearchParameters } from '@/types/Search'
import { API_URL, FilterModalType, SearchSection, wizardLabels } from '@/lib/sharedConstants'
import { usePersistedPageLoaded } from '@/lib/hooks'
import { SearchPageContext } from '@/components/pages/search-page/SearchPageContext'
import { fetchLabels } from '@/lib/frontent-api'
import { MapTypesLngLat, MapTypesLngLatBounds } from '@/components/ui/search/map-types'
import { StarsValues } from '@/components/ui/wizard/filter/Stars'
import { allFilters } from '@/lib/utils/configUtils'
import { useTranslations } from 'next-intl'
import { compact } from 'lodash'
import _flatten from 'lodash/flatten'

enum SearchTypes {
  DESTINATIONS = 'destinations',
  TOPICS = 'topics',
}

const SEARCH_DELAY = 0

let searchDelayTimerId: number | undefined

const SEARCH_RESULT_ITEM_CATEGORY_TO_SEARCH_SECTION = {
  [SearchResultItemCategory.Hotel]: SearchSection.Hotel,
  [SearchResultItemCategory.Region]: SearchSection.Region,
  [SearchResultItemCategory.Country]: SearchSection.Country,
  [SearchResultItemCategory.CountryGroup]: SearchSection.CountryGroup,
  [SearchResultItemCategory.Location]: SearchSection.Location,
}

const RATING_MAPPING = {
  [RatingValues.OK]: 3,
  [RatingValues.GOOD]: 3.5,
  [RatingValues.VERY_GOOD]: 4,
  [RatingValues.EXCELLENT]: 4.5,
}

const STARS_MAPPING = {
  [StarsValues.THREE]: 3,
  [StarsValues.FOUR]: 4,
  [StarsValues.FIVE]: 5,
}

export interface Dictionary<T> {
  [index: string]: T
}

function autoCompleteSearch(
  dispatch: Dispatch<AnyAction>,
  query: string,
  type: SearchTypes,
  formatResult: (apiResponse: Dictionary<SearchItem[]>) => SearchResult[],
  setSearchResult: ActionCreatorWithOptionalPayload<{
    searchResult: SearchResult[]
    searchTerm: string
  }>,
  domain: Domain,
  currency: Currency,
): void {
  if (searchDelayTimerId) {
    clearTimeout(searchDelayTimerId)
  }

  searchDelayTimerId = window.setTimeout(() => {
    fetchWithAbort(
      `${API_URL}search/autocomplete/${type}/?domain=${domain}&query=${encodeURIComponent(query || '')}`,
      { domain, currency },
    )
      .then((r) => r.json())
      .then((r: SearchItem[]): SearchResult[] => {
        return formatResult(
          _groupBy(r, (v: SearchItem) =>
            v.type !== SearchResultItemCategory.TopicType ? v.type : SearchResultItemCategory.Topic,
          ),
        )
      })
      .then((r) => {
        return r
      })
      .then((r) => dispatch(setSearchResult({ searchResult: r, searchTerm: query })))
      .catch((error: Error) => {
        if (error.name !== 'AbortError') {
          console.log(error)
        }
      })
  }, SEARCH_DELAY)
}

function undefIfInvalid(startDate: moment.Moment): Moment | undefined {
  return startDate.isValid() ? startDate : undefined
}

function getLocationFilter(config: ExtendedDomainConfiguration): LocationFilterElement[] {
  const searchParams = new URLSearchParams(window.location.search)

  return Object.values(LocationFilterElementType).flatMap((filterType) =>
    searchParams.has(filterType)
      ? _compact(
          searchParams
            .get(filterType)
            ?.split('_')
            .map((e) => allFilters(config).find((f) => f.value === e && f.type === filterType)),
        )
      : [],
  )
}

function prefillSearchFilter(config: ExtendedDomainConfiguration): void {
  const searchParams = new URLSearchParams(window.location.search)
  const numberOfGuests = searchParams.get(SEARCH_PARAMETER_KEYS.b) as string
  const rating = searchParams.get(SEARCH_PARAMETER_KEYS.rating) as string
  const stars = searchParams.get(SEARCH_PARAMETER_KEYS.stars) as string
  const maximumPrice = searchParams.get(SEARCH_PARAMETER_KEYS.pl) as string
  const order = searchParams.get(SEARCH_PARAMETER_KEYS.o) as string
  window.store?.dispatch(
    setState({
      numberOfGuests: +numberOfGuests || MIN_NUMBER_OF_GUESTS,
      locationFilter: getLocationFilter(config),
      rating: _findKey(RATING_MAPPING, (v) => v === +rating) as RatingValues,
      stars: _findKey(STARS_MAPPING, (v) => v === +stars) as StarsValues,
      maximumPrice: searchParams.get(SEARCH_PARAMETER_KEYS.pl)
        ? config.searchFilters.price[config.currency][+maximumPrice - 1]
        : undefined,
      order: searchParams.get(SEARCH_PARAMETER_KEYS.o) ? +order : undefined,
    }),
  )
  if (searchParams.get(SEARCH_PARAMETER_KEYS.lng_se)) {
    const searchBounds = MapTypesLngLatBounds.fromNwSe(
      new MapTypesLngLat(
        +(searchParams.get(SEARCH_PARAMETER_KEYS.lng_nw) || 0),
        +(searchParams.get(SEARCH_PARAMETER_KEYS.lat_nw) || 0),
      ),
      new MapTypesLngLat(
        +(searchParams.get(SEARCH_PARAMETER_KEYS.lng_se) || 0),
        +(searchParams.get(SEARCH_PARAMETER_KEYS.lat_se) || 0),
      ),
    )
    window.store?.dispatch(setSearchBounds(searchBounds))
  }
}

function flexibleTravelPeriodSelected(searchParams: URLSearchParams): boolean {
  return (
    searchParams.get(SEARCH_PARAMETER_KEYS.dateExact) !== '1' &&
    (searchParams.has(SEARCH_PARAMETER_KEYS.month) ||
      (searchParams.has(SEARCH_PARAMETER_KEYS.dmax) &&
        searchParams.has(SEARCH_PARAMETER_KEYS.dmin)))
  )
}

export function prefillTimeSearchFromQueryData(
  searchParams: URLSearchParams,
  locale: string,
): void {
  if (flexibleTravelPeriodSelected(searchParams)) {
    const selectedMonths = searchParams.get(SEARCH_PARAMETER_KEYS.month)?.split('_') || []
    window.store?.dispatch(
      setState({
        selectedOptionPeriods: TRAVEL_PERIOD_OPTIONS.filter((option) => {
          return selectedMonths.includes(option.month.toString())
        }),
        selectedOptionDuration: DATE_FLEXIBILITY_OPTIONS_FLEXIBLE.find(
          (o) => o.max.toString() === searchParams.get(SEARCH_PARAMETER_KEYS.dmax),
        ),
        flexibleTravelPeriodSelected: true,
      }),
    )
  } else {
    const startDate = moment(searchParams.get(SEARCH_PARAMETER_KEYS.df))
      .startOf('day')
      .locale(locale)
    const endDate = moment(searchParams.get(SEARCH_PARAMETER_KEYS.dt)).startOf('day').locale(locale)

    if (startDate && !startDate.isBefore(moment().startOf('day'))) {
      window.store?.dispatch(
        setState({
          startDate: undefIfInvalid(startDate),
          endDate: undefIfInvalid(endDate),
          selectedOptionCalendarFlexibility: DATE_FLEXIBILITY_OPTIONS_CALENDAR.find(
            (d) => d.max.toString() === searchParams.get(SEARCH_PARAMETER_KEYS.dateFlexibility),
          ),
          flexibleTravelPeriodSelected: flexibleTravelPeriodSelected(searchParams),
        }),
      )
    }
  }
}

function prefillFromQueryData(domain: Domain, config: ExtendedDomainConfiguration): Promise<void> {
  const searchParams = new URLSearchParams(window.location.search)
  return fetchLabels(searchParams, domain, config)
    .then((labels) => {
      window.store?.dispatch(
        setState({
          selectedLocationItems: labels.search.destinations,
          selectedTopicItems: labels.search.topics,
          flexibleTravelPeriodSelected: flexibleTravelPeriodSelected(searchParams),
        }),
      )

      prefillTimeSearchFromQueryData(searchParams, domain)
    })
    .then(() => prefillSearchFilter(config))
    .finally(() => window.store?.dispatch(setState({ prefilled: true })))
}

function loadSavedStoreData(props: WizardImplementationProps): void {
  const baseData = {
    selectedLocationItems: props.destinations || [],
    selectedTopicItems: props.topics || [],
  }

  let additionalCalendarData

  window.store?.dispatch(
    setState({ ...baseData, ...additionalCalendarData, prefilled: true } as Partial<WizardState>),
  )

  switch (props.deleteValue) {
    case 0:
      window.store?.dispatch(resetLocations())
      break
    case 1:
      window.store?.dispatch(resetTopic())
      break
    case 2:
      window.store?.dispatch(resetTravelPeriodSearch())
      break
  }

  window.store?.dispatch(setSavedSearchLoaded(undefined))
}

interface WizardImplementationProps extends SearchProps {
  searchPage: boolean
  activeStep: number | undefined
  deleteValue: 0 | 1 | 2 | undefined
  searchWizard: boolean
  currency: Currency
}

export default function WizardImplementation(props: WizardImplementationProps): ReactElement {
  const translate = useTranslations('Wizard')
  const config = useContext(ConfigContext)!
  const locale = useContext(ConfigContext)!.locale
  const { setSearchOptionsType, searchOptionsType } = useContext(SearchPageContext)

  useEffect(() => {
    moment.locale(locale)
  }, [locale])

  usePersistedPageLoaded(() => {
    if (!props.searchWizard) {
      loadSavedStoreData(props)
    }
  })

  const store = useMemo(() => {
    return createWizardStore(props, translate)
  }, [])
  const [stateHasBeenPrefilled, setStateHasBeenPrefilled] = useState(false)
  const domain = config.domain

  useEffect(() => {
    if (!props.searchWizard) {
      loadSavedStoreData(props)
      store.dispatch(setActiveStep(props.activeStep))
    } else {
      prefillFromQueryData(domain, config)
        .then(() => setStateHasBeenPrefilled(true))
        .catch(() => false)
    }
  }, [store])

  return (
    <Provider store={store}>
      <WrappedWizard
        activeStep={props.activeStep}
        deleteValue={props.deleteValue}
        searchWizard={props.searchWizard}
        showSearchOptionsType={searchOptionsType}
        setShowSearchOptionsType={setSearchOptionsType}
        closeSearchOptions={() => setSearchOptionsType(undefined)}
        currency={props.currency}
        stateHasBeenPrefilled={stateHasBeenPrefilled}
        searchPage={props.searchPage}
      />
      {props.searchWizard && (
        <SearchOptions
          closeWizard={() => {
            window.store?.dispatch(setActiveStep(undefined))
          }}
          currency={props.currency}
        />
      )}
    </Provider>
  )
}

function formatLocationSearchResult(apiResponse: Dictionary<SearchItem[]>): SearchResult[] {
  return Object.entries(apiResponse).map((entry) => ({
    type: SEARCH_RESULT_ITEM_CATEGORY_TO_SEARCH_SECTION[entry[0]] as SearchSection,
    results: entry[1],
  }))
}

function formatTopicSearchResult(apiResponse: Dictionary<SearchItem[]>): SearchResult[] {
  return [{ type: SearchSection.Topic, results: _flatten(Object.values(apiResponse)) || [] }]
}

interface WrappedWizardProps {
  searchPage: boolean
  activeStep: number | undefined
  deleteValue: 0 | 1 | 2 | undefined
  searchWizard: boolean | undefined
  showSearchOptionsType: FilterModalType | undefined
  setShowSearchOptionsType: (show: FilterModalType) => void
  closeSearchOptions: () => void
  currency: Currency
  stateHasBeenPrefilled: boolean
}

interface SearchPageCurrentSearchPartProps {
  searchTerm: string
  forStep: number
}

function SearchPageCurrentSearchPart(props: SearchPageCurrentSearchPartProps): ReactElement {
  const dispatch = useDispatch()

  return (
    <span
      className="mx-1 overflow-hidden text-ellipsis whitespace-nowrap first:ml-2 last:mr-2 md:hidden"
      onClick={(event) => {
        event.stopPropagation()
        dispatch(setActiveStep(props.forStep))
      }}
    >
      {props.searchTerm}
    </span>
  )
}

function MobileSearchPageCurrentSearchDisplaySeparator(props: {
  onClick: (event) => void
}): ReactElement {
  return (
    <span onClick={props.onClick} className="md:hidden">
      {' '}
      -{' '}
    </span>
  )
}

function addSeparator(
  r: ReactElement[],
  a: ReactElement,
  currentIndex: number,
  dispatch: Dispatch,
): ReactElement[] {
  return r.concat(
    a,
    <MobileSearchPageCurrentSearchDisplaySeparator
      key={`separator-${currentIndex}`}
      onClick={(event: MouseEvent) => {
        event.stopPropagation()
        dispatch(setActiveStep(currentIndex))
      }}
    />,
  )
}

function MobileSearchPageCurrentSearchDisplay(props: {
  searchWizard: boolean | undefined
  activeStep: number | undefined
  searchPageCurrentSearchPartProps: SearchPageCurrentSearchPartProps[]
  placeholder: string
}): ReactElement {
  const dispatch = useDispatch()

  return (
    <>
      {props.searchWizard &&
        props.activeStep === undefined &&
        props.searchPageCurrentSearchPartProps
          .map((part) => <SearchPageCurrentSearchPart {...part} key={part.forStep} />)
          .reduce((r: ReactElement[], a, i) => addSeparator(r, a, i, dispatch), [])
          .slice(0, -1)}
    </>
  )
}

function WrappedWizard(props: WrappedWizardProps): ReactElement {
  const [searchTermChanged, setSearchTermChanged] = useState(false)
  const topicSearchTerm = useWizardSelector<string>((s) => s.topicSearchTerm)
  const locationSearchTerm = useWizardSelector<string>((s) => s.locationSearchTerm)
  const travelPeriodSearchTerm = useWizardSelector<string>((s) => s.travelPeriodSearchTerm)
  const selectedLocationItems = useWizardSelector<SearchItem[]>((s) => s.selectedLocationItems)
  const selectedTopicItems = useWizardSelector<SearchItem[]>((s) => s.selectedTopicItems)
  const locationSearchResult = useWizardSelector<SearchResult[]>((s) => s.locationSearchResult)
  const topicSearchResult = useWizardSelector<SearchResult[]>((s) => s.TopicSearchResult)
  const selectedOptionPeriods = useWizardSelector<Month[] | undefined>(
    (s) => s.selectedOptionPeriods,
  )
  const selectedOptionDuration = useWizardSelector<Duration | undefined>(
    (s) => s.selectedOptionDuration,
  )
  const startDate = useWizardSelector<Moment | undefined>((s) => s.startDate)
  const endDate = useWizardSelector<Moment | undefined>((s) => s.endDate)
  const topicSearchResultSearchTerm = useWizardSelector<string>(
    (s) => s.topicSearchResultSearchTerm,
  )
  const locationSearchResultSearchTerm = useWizardSelector<string>(
    (s) => s.locationSearchResultSearchTerm,
  )
  const flexibleTravelPeriodSelected = useWizardSelector<boolean | undefined>(
    (s) => s.flexibleTravelPeriodSelected,
  )
  const selectedOptionCalendarFlexibility = useWizardSelector<Duration | undefined>(
    (s) => s.selectedOptionCalendarFlexibility,
  )
  const numberOfGuests = useWizardSelector((state) => state.numberOfGuests)
  const rating = useWizardSelector((state) => state.rating)
  const stars = useWizardSelector((state) => state.stars)
  const locationFilters = useWizardSelector((state) => state.locationFilter)
  const maximumPrice = useWizardSelector<string | undefined>((state) => state.maximumPrice)
  const activeStep = useWizardSelector<number | undefined>((s) => s.activeStep)
  const order = useWizardSelector((state) => state.order)
  const searchBounds = useWizardSelector((state) => state.searchBounds)
  const wizardRef = useRef<HTMLDivElement>(null)
  const config = useContext(ConfigContext)!
  const priceList = config.searchFilters.price
  const dispatch = useDispatch<Dispatch<AnyAction>>()
  const translate = useTranslations('Wizard')
  const [additionalParams, setAdditionalParams] = useState<Record<string, string>>({})
  const savedSearchLoaded = useWizardSelector<boolean>((s) => s.savedSearchLoaded)
  const prefilled = useWizardSelector<boolean>((s) => s.prefilled)
  const resetIndex = useWizardSelector<number>((s) => s.resetIndex)

  useEffect(() => {
    if (!travelPeriodSearchTerm && props.stateHasBeenPrefilled && props.searchWizard) {
      dispatch(setActiveStep(WizardStep.TRAVEL_PERIOD))
    }
  }, [props.stateHasBeenPrefilled])

  useEffect(() => {
    const searchParams = getCurrentSearchParams()
    Object.keys(SEARCH_PARAMETER_KEYS).forEach((key) => searchParams.delete(key))
    setAdditionalParams(Object.fromEntries(searchParams.entries()))
  }, [])

  useEffect(() => {
    if (activeStep === 2) {
      window.document.documentElement.classList.remove('keyboardOpen')
    }
  }, [activeStep])

  useEffect(() => {
    window.addEventListener('click', () => {
      if (!onMobile()) {
        dispatch(setActiveStep(undefined))
      }
    })
  }, [dispatch, props.activeStep])

  useEffect(() => {
    if (activeStep !== undefined) {
      if (props.searchWizard ? onMobile() : onMobileOrTablet()) {
        lockScrollableBody()
      }
    } else {
      unlockScrollableBody()
    }
  }, [activeStep])

  useEffect(() => {
    if (topicSearchTerm === '') {
      dispatch(
        setTopicSearchResult({
          searchResult: [
            { type: SearchSection.TopTopics, results: config.recommendedTravelTopics },
          ],
          searchTerm: '',
        }),
      )
      return
    }

    autoCompleteSearch(
      dispatch,
      topicSearchTerm,
      SearchTypes.TOPICS,
      formatTopicSearchResult,
      setTopicSearchResult,
      config.domain,
      props.currency,
    )
  }, [topicSearchTerm, dispatch, config.recommendedTravelTopics])

  useEffect(() => {
    if (locationSearchTerm === '') {
      dispatch(
        setLocationSearchResult({
          searchResult: [
            {
              type: SearchSection.TopCountriesGroup,
              results: config.recommendedDestinations.filter(
                (d) => d.type === SearchResultItemCategory.CountryGroup,
              ),
            },
            {
              type: SearchSection.TopCountries,
              results: config.recommendedDestinations.filter(
                (d) => d.type === SearchResultItemCategory.Country,
              ),
            },
            {
              type: SearchSection.TopRegions,
              results: config.recommendedDestinations.filter(
                (d) => d.type === SearchResultItemCategory.Region,
              ),
            },
            {
              type: SearchSection.TopLocations,
              results: config.recommendedDestinations.filter(
                (d) => d.type === SearchResultItemCategory.Location,
              ),
            },
            {
              type: SearchSection.TopHotels,
              results: config.recommendedDestinations.filter(
                (d) => d.type === SearchResultItemCategory.Hotel,
              ),
            },
          ],
          searchTerm: '',
        }),
      )
      return
    }

    autoCompleteSearch(
      dispatch,
      locationSearchTerm,
      SearchTypes.DESTINATIONS,
      formatLocationSearchResult,
      setLocationSearchResult,
      config.domain,
      props.currency,
    )
  }, [locationSearchTerm, dispatch, config.recommendedDestinations])

  useEffect(() => {
    if (activeStep === WizardStep.SEARCH && savedSearchLoaded) {
      search()
    }
  }, [activeStep, savedSearchLoaded])

  function triggerSearch(): void {
    if (props.searchWizard && prefilled) {
      const searchParams = getSearchParameters()
      const searchFilterParams = getSearchFilterParameters()
      const keys = Object.keys({ ...searchParams, ...searchFilterParams })

      const params: SearchParameters = {}

      keys.forEach((key: SEARCH_PARAMETER_KEYS) => {
        if (typeof searchParams[key] !== 'number') {
          params[key] = compact([
            (searchParams[key] as string) || undefined,
            (searchFilterParams[key] as string) || undefined,
          ]).join('_')
        } else {
          params[key] = searchParams[key] || searchFilterParams[key]
        }
      })

      window.dispatchEvent(
        new CustomEvent(SEARCH_PARAMETERS_UPDATED_EVENT_TYPE, {
          detail: params,
        }),
      )
      if (props.stateHasBeenPrefilled) {
        if (!params['pl']) {
          delete params.pl
        }

        const parameterSeparator = Object.keys(params).length ? '?' : ''

        const searchParams = new URLSearchParams(additionalParams)
        addParamsToSearchParams(searchParams, params)

        window.history.replaceState(
          {},
          '',
          `/search/${parameterSeparator}${searchParams.toString()}`,
        )
      }
    }
  }

  function getCurrentSearchParams(): URLSearchParams {
    const currentUrl = new URL(window.location.href)
    return currentUrl.searchParams
  }

  function addParamsToSearchParams(
    searchParams: URLSearchParams,
    additionalParams: Record<string, number | string>,
  ): URLSearchParams {
    Object.entries(additionalParams).forEach(([key, value]) => {
      searchParams.set(key, value.toString())
    })
    return searchParams
  }

  useEffect(() => {
    if (prefilled) {
      setSearchTermChanged(true)
    }
  }, [
    selectedLocationItems,
    selectedTopicItems,
    selectedOptionPeriods,
    selectedOptionDuration,
    startDate,
    endDate,
    selectedOptionCalendarFlexibility,
  ])

  function triggerSearchIfSearchChanged() {
    if (searchTermChanged) {
      triggerSearch()
    }

    setSearchTermChanged(false)
  }

  useEffect(triggerSearchIfSearchChanged, [activeStep])

  useEffect(triggerSearch, [
    numberOfGuests,
    rating,
    stars,
    locationFilters,
    maximumPrice,
    order,
    searchBounds,
    prefilled,
    resetIndex,
  ])

  function formatSearchItems(items: SearchItem[]): string {
    return items.map((i) => i.value).join('_')
  }

  function getLocationSearchItems(category: SearchResultItemCategory): SearchItem[] {
    return selectedLocationItems.filter((li) => li.type === category)
  }

  function getTimeSearchQueryObject(): Record<string, string | number> {
    return getTimeSearchQuery(
      selectedOptionDuration,
      flexibleTravelPeriodSelected,
      selectedOptionCalendarFlexibility,
      selectedOptionPeriods,
      startDate,
      endDate,
    )
  }

  function addTopicParameter(
    result: SearchParameters,
    [key, category]: [SEARCH_PARAMETER_KEYS, SearchResultItemCategory | LocationFilterElementType],
  ): SearchParameters {
    const items = selectedTopicItems.filter((i) => i.type === category)
    if (items.length) {
      result[key] = formatSearchItems(items)
    }
    return result
  }

  function addLocationParameter(
    result: SearchParameters,
    [key, category]: [SEARCH_PARAMETER_KEYS, SearchResultItemCategory],
  ) {
    const items = getLocationSearchItems(category).filter((i) => i.type === category)
    if (items.length) {
      result[key] = formatSearchItems(items)
    }
    return result
  }

  function getMappedTopicsAndLocationSearchItems(): SearchParameters {
    const topicMapping = {
      [SEARCH_PARAMETER_KEYS.t]: SearchResultItemCategory.Topic,
      [SEARCH_PARAMETER_KEYS.tt]: SearchResultItemCategory.TopicType,
      [SEARCH_PARAMETER_KEYS.mp]: LocationFilterElementType.MealPlan,
      [SEARCH_PARAMETER_KEYS.rt]: LocationFilterElementType.RoomType,
      [SEARCH_PARAMETER_KEYS.allotment]: LocationFilterElementType.Allotment,
      [SEARCH_PARAMETER_KEYS.prop]: LocationFilterElementType.Property,
      [SEARCH_PARAMETER_KEYS.stars]: LocationFilterElementType.Stars,
    }

    const locationMapping = {
      [SEARCH_PARAMETER_KEYS.r]: SearchResultItemCategory.Region,
      [SEARCH_PARAMETER_KEYS.cg]: SearchResultItemCategory.CountryGroup,
      [SEARCH_PARAMETER_KEYS.c]: SearchResultItemCategory.Country,
      [SEARCH_PARAMETER_KEYS.h]: SearchResultItemCategory.Hotel,
      [SEARCH_PARAMETER_KEYS.l]: SearchResultItemCategory.Location,
    }

    const result = Object.entries(topicMapping).reduce(addTopicParameter, {})

    return Object.entries(locationMapping).reduce(addLocationParameter, result)
  }

  function getSearchParameters(): SearchParameters {
    return removeUndefinedEntries({
      ...getMappedTopicsAndLocationSearchItems(),
      ...getTimeSearchQueryObject(),
    })
  }

  function getLocationFilterSearchSubset(type: LocationFilterElementType): string {
    return locationFilters
      .filter((locationFilter) => locationFilter.type === type)
      .map((locationFilter) => locationFilter.value)
      .join('_')
  }

  function getSearchFilterParameters(): SearchParameters {
    return removeUndefinedEntries({
      [SEARCH_PARAMETER_KEYS.b]: numberOfGuests === 0 ? undefined : numberOfGuests,
      [SEARCH_PARAMETER_KEYS.rating]: rating ? RATING_MAPPING[rating] : undefined,
      [SEARCH_PARAMETER_KEYS.stars]: stars ? STARS_MAPPING[stars] : undefined,
      [LocationFilterElementType.Property]: getLocationFilterSearchSubset(
        LocationFilterElementType.Property,
      ),
      [LocationFilterElementType.MealPlan]: getLocationFilterSearchSubset(
        LocationFilterElementType.MealPlan,
      ),
      [LocationFilterElementType.RoomType]: getLocationFilterSearchSubset(
        LocationFilterElementType.RoomType,
      ),
      [LocationFilterElementType.Allotment]: getLocationFilterSearchSubset(
        LocationFilterElementType.Allotment,
      ),
      [SEARCH_PARAMETER_KEYS.pl]:
        maximumPrice && props.currency && priceList[props.currency]
          ? priceList[props.currency].findIndex((v) => v === maximumPrice) + 1
          : undefined,
      [SEARCH_PARAMETER_KEYS.o]: order?.toString(),
      [SEARCH_PARAMETER_KEYS.lat_nw]: searchBounds?.getNorthWest().lat,
      [SEARCH_PARAMETER_KEYS.lng_nw]: searchBounds?.getNorthWest().lng,
      [SEARCH_PARAMETER_KEYS.lat_se]: searchBounds?.getSouthEast().lat,
      [SEARCH_PARAMETER_KEYS.lng_se]: searchBounds?.getSouthEast().lng,
    })
  }

  function search(): void {
    const params = new URLSearchParams(getSearchParameters() as Record<string, string>)
    window.location.href = `${location.protocol}//${location.host}/search/?${params.toString()}`
  }

  function searchOptionsToString(selectedItems: SearchItem[]): string {
    return selectedItems.map((i) => i.label || i.text).join(', ')
  }

  function getSearchPageCurrentSearchPart(
    forStep: number,
    s: string,
  ): SearchPageCurrentSearchPartProps | null {
    if (!s) {
      return null
    }

    return { forStep: forStep, searchTerm: s }
  }

  const searchPageCurrentSearchParts = _compact([
    getSearchPageCurrentSearchPart(
      0,
      searchOptionsToString(selectedLocationItems) || translate('mobilePlaceholderDestination'),
    ),
    getSearchPageCurrentSearchPart(
      1,
      searchOptionsToString(selectedTopicItems) || translate('mobilePlaceholderTopic'),
    ),
    getSearchPageCurrentSearchPart(
      2,
      travelPeriodSearchTerm || translate('mobilePlaceholderTravelPeriod'),
    ),
  ])

  function travelPeriodSelected(): boolean {
    return flexibleTravelPeriodSelected ? !!travelPeriodSearchTerm : !!(startDate && endDate)
  }

  return (
    <WizardWrapper
      wizardRef={wizardRef}
      search={search}
      activeStep={activeStep}
      onMobileEntryClick={() => {
        dispatch(setActiveStep(0))
      }}
      searchWizard={props.searchWizard}
      closeSearchOptions={props.closeSearchOptions}
      showSearch={() => activeStep === undefined && dispatch(setActiveStep(0))}
      placeHolderShown={!!searchPageCurrentSearchParts.length}
    >
      <MobileSearchPageCurrentSearchDisplay
        searchWizard={props.searchWizard}
        activeStep={activeStep}
        searchPageCurrentSearchPartProps={searchPageCurrentSearchParts}
        placeholder={translate('destinationLabelMobile')}
      />
      <WizardInput
        {...wizardLabels.destinationInputProps}
        scrollIntoViewOnLoad={true}
        {...DESTINATION_INPUT_PROPS}
        step={WizardStep.DESTINATION}
        searchTerm={locationSearchTerm}
        setSearchTerm={setLocationSearchTerm}
        selectedValues={searchOptionsToString(selectedLocationItems)}
        resetInput={resetLocations}
        searchWizard={props.searchWizard}
        searchPage={props.searchPage}
        detailsBox={
          <SearchBox
            searchWizard={props.searchWizard}
            step={WizardStep.DESTINATION}
            searchResults={locationSearchResult}
            selectedItems={selectedLocationItems}
            setSelectedItems={setSelectedLocationItems}
            searchTerm={locationSearchTerm}
            resultSearchTerm={locationSearchResultSearchTerm}
            nextButtonActiveLandingPage={!!selectedLocationItems.length}
            hideSkipButton={
              (!props.searchWizard && selectedLocationItems.length > 0) || props.searchWizard
            }
          />
        }
      />
      <WizardInput
        {...wizardLabels.topicInputProps}
        {...TOPIC_INPUT_PROPS}
        searchTerm={topicSearchTerm}
        setSearchTerm={setTopicSearchTerm}
        selectedValues={searchOptionsToString(selectedTopicItems)}
        step={WizardStep.TOPIC}
        resetInput={resetTopic}
        searchWizard={props.searchWizard}
        searchPage={props.searchPage}
        detailsBox={
          <SearchBox
            searchWizard={props.searchWizard}
            step={WizardStep.TOPIC}
            searchResults={topicSearchResult}
            selectedItems={selectedTopicItems}
            setSelectedItems={setSelectedTopicItems}
            searchTerm={topicSearchTerm}
            resultSearchTerm={topicSearchResultSearchTerm}
            nextButtonActiveLandingPage={!!selectedTopicItems.length}
            hideSkipButton={
              (!props.searchWizard && selectedTopicItems.length > 0) || props.searchWizard
            }
          />
        }
      />
      <WizardInput
        {...wizardLabels.travelPeriodInputProps}
        {...TRAVEL_PERIOD_INPUT_PROPS}
        searchTerm={travelPeriodSearchTerm}
        selectedValues={travelPeriodSearchTerm}
        step={WizardStep.TRAVEL_PERIOD}
        searchWizard={props.searchWizard}
        hideRightInputBorder={true}
        searchPage={props.searchPage}
        detailsBox={
          <TravelPeriod
            step={WizardStep.TRAVEL_PERIOD}
            search={search}
            searchWizard={props.searchWizard}
            nextButtonActiveLandingPage={props.searchWizard || travelPeriodSelected()}
            primaryActionLabel={props.searchWizard ? 'closeButton' : undefined}
            absolutePositioned={false}
            hideSkipButton={props.searchWizard || travelPeriodSelected() || props.searchWizard}
          />
        }
        resetInput={resetTravelPeriodSearch}
        disableInputField={true}
        hideInputOnMobile={true}
      />
      {/*<WizardInput*/}
      {/*  label="Gäste"*/}
      {/*  placeholder="Wer?"*/}
      {/*  name="guests"*/}
      {/*  searchTerm={guestSearchTerm}*/}
      {/*  selectedValues={guestSearchTerm}*/}
      {/*  step={3}*/}
      {/*  detailsBox={<GuestDetails step={3} search={search} />}*/}
      {/*  resetInput={resetGuestSearch}*/}
      {/*  disableInputField={true}*/}
      {/*/>*/}
    </WizardWrapper>
  )
}

export function removeUndefinedEntries<
  T extends Record<string, string> | Record<string, string | number | undefined>,
>(obj: T): T {
  Object.keys(obj).forEach((key) => {
    if (obj[key] === undefined || obj[key] === '') {
      delete obj[key]
    }
  })

  return obj
}

export function getTimeSearchQuery(
  selectedOptionDuration: Duration | undefined,
  flexibleTravelPeriodSelected: boolean | undefined,
  selectedOptionCalendarFlexibility: Duration | undefined,
  selectedOptionPeriods: Month[] | undefined,
  startDate: Moment | undefined,
  endDate: Moment | undefined,
): SearchParameters {
  return getTimeSearchQueryGeneral(
    selectedOptionDuration,
    flexibleTravelPeriodSelected,
    selectedOptionCalendarFlexibility,
    selectedOptionPeriods,
    startDate?.format(),
    endDate?.format(),
  )
}
